import React, { useEffect, useRef, useState } from 'react';
import { Icon, Loader, Message, Tab, type TabProps } from 'semantic-ui-react';

import Food from '../common/food';
import FoodCard from '../common/foodCard';

import Activity from './activity';
import Alerts from './alerts';
import type { AlertsHandle } from './alerts';
import Members from './members';
import Connect from './messages';
import Vendors from './vendors';

const ALERT_TAB_INDEX = 4;
const FOOD_CARD_TAB_INDEX = 3;

type HomeProps = {
  setActiveAlertsCount: (count: number) => void;
  setTab: (tab: number) => void;
  tab: number;
};

function Home(props: HomeProps) {
  const alertRef = useRef<AlertsHandle>(null);
  const [activeTab, setActiveTab] = React.useState(0);
  const [loading, _setLoading] = useState<boolean>(false);
  const [error, setError] = useState([]);

  useEffect(() => {
    setActiveTab(props.tab);
    if (props.tab === 4)
      // Bell click
      alertRef.current!.refreshAlerts();
  }, [props.tab]);

  //Opens Slected Patient in a new Chrome Tab
  //taskId is used to set the currentTaskId when we open member page
  // `unknown` is the only parameter type every child can supply: `home/messages`
  // declares `selectedPatient?: Patient`, the four food screens pass a bare `unknown`.
  const showNewPatient = (selectedPatient?: unknown, taskId?: string, patientId?: string) => {
    const patient = selectedPatient as fhir4.Patient | undefined;
    if (taskId) {
      window.open(`/patients/${patient!.id}?taskId=${taskId}`, patient!.id);
    } else if (patientId) {
      window.open(`/patients/${patientId}`, patientId);
    } else {
      window.open(`/patients/${patient!.id}`, patient!.id);
    }
  };

  const panes = React.useMemo(
    () => [
      {
        menuItem: { color: 'teal', content: <>MEMBERS</>, icon: 'users', name: 'MEMBERS', key: 'members' },
        pane: {
          key: 'members',
          attached: false,
          content: <Members onPatientSelected={showNewPatient} />,
          size: 'massive',
        },
      },
      {
        menuItem: { color: 'teal', content: <>ACTIVITY</>, icon: 'tasks', name: 'ACTIVITY', key: 'activity' },
        pane: {
          key: 'activity',
          attached: false,
          content: <Activity onPatientSelected={showNewPatient} />,
          size: 'massive',
        },
      },
      {
        menuItem: { color: 'teal', content: <>FOOD</>, icon: 'food', name: 'FOOD', key: 'food' },
        pane: {
          key: 'food',
          attached: false,
          content: <Food onPatientSelected={showNewPatient} patientId={null} org={null} />,
          size: 'massive',
        },
      },
      {
        menuItem: { color: 'teal', content: <>FOOD CARD</>, icon: 'credit card', name: 'FOOD_CARD', key: 'foodcard' },
        pane: {
          key: 'foodcard',
          attached: false,
          content: (
            <FoodCard
              onPatientSelected={showNewPatient}
              patientId={null}
              org={null}
              fetchOrders={activeTab === FOOD_CARD_TAB_INDEX}
            />
          ),
          size: 'massive',
        },
      },
      {
        menuItem: { color: 'teal', content: <>VENDORS</>, icon: 'shipping', name: 'VENDORS', key: 'vendors' },
        pane: { key: 'vendors', attached: false, content: <Vendors />, size: 'massive' },
      },
      {
        menuItem: { color: 'teal', content: <>ALERTS</>, icon: 'bell', name: 'ALERTS', key: 'alerts' },
        pane: {
          key: 'alerts',
          attached: false,
          content: (
            <Alerts
              onPatientSelected={showNewPatient}
              ref={alertRef}
              setActiveAlertsCount={props.setActiveAlertsCount}
            />
          ),
        },
      },
      //This really needs refactoring
      {
        menuItem: {
          color: 'teal',
          content: (
            <>
              CONTACT CENTER &nbsp;&nbsp; <Icon name="circle" color="red" size="small" />
            </>
          ),
          icon: 'phone',
          name: 'CONNECT',
          key: 'connect',
        },
        pane: {
          key: 'connect',
          attached: false,
          content: <Connect onPatientSelected={showNewPatient} />,
        },
      },
      /* {
      menuItem: { color: 'teal', content: <>DASHBOARD</>, icon: 'chart area', name: 'DASHBOARD', key: 'dashboard' },
      pane: {
        key: 'dashboard',
        attached: false,
        content: <Stats onPatientSelected={showNewPatient} />,
        size: 'massive',
      },
    }, */
    ],
    [activeTab, props.setActiveAlertsCount]
  );

  const handleTabChange = (e: React.MouseEvent<HTMLDivElement>, { activeIndex, panes }: TabProps) => {
    setError([]);
    if ((activeIndex as number) < panes!.length) {
      setActiveTab(activeIndex as number);
      props.setTab(activeIndex as number);
    } else {
      setActiveTab(0);
      props.setTab(0);
    }

    if (activeIndex === ALERT_TAB_INDEX) {
      alertRef.current!.refreshAlerts();
    }
  };

  return (
    <>
      {loading && <Loader active inline="centered" content={'Fetching Member Data...'} />}
      {error.length > 0 && <Message error header="Error" list={error} />}
      {
        <Tab
          className="cb_home"
          menu={{ secondary: true, pointing: true }}
          renderActiveOnly={false}
          activeIndex={error.length === 0 ? activeTab : 0}
          panes={panes}
          onTabChange={handleTabChange}
          style={{ marginLeft: '10px' }}
        />
      }
    </>
  );
}

export default Home;
